import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';
import {
  ArrowsIn,
  ArrowsOut,
  ArrowsHorizontal,
  ArrowsVertical,
  CaretDoubleDown,
  CaretDoubleUp,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  SpinnerGap,
  TreeStructure,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import OrganizationChart from '../components/organizationChart/OrganizationChart';
import OrganizationChartFilters from '../components/organizationChart/OrganizationChartFilters';
import UnassignedUsersPanel from '../components/organizationChart/UnassignedUsersPanel';
import { collectNodeIds } from '../utils/organizationChart';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_FILTERS = {
  organization: 'all',
  department: 'all',
  team: 'all',
  locationType: 'all',
  active: 'active',
  search: '',
};

const OrganizationChartPage = () => {
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [layout, setLayout] = useState('vertical');
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search || ''), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const fetchChart = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filters.organization && filters.organization !== 'all') params.organization = filters.organization;
      if (filters.department && filters.department !== 'all') params.department = filters.department;
      if (filters.team && filters.team !== 'all') params.team = filters.team;
      if (filters.locationType && filters.locationType !== 'all') params.location_type = filters.locationType;
      if (filters.active) params.active = filters.active;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const res = await axios.get(`${API_URL}/api/organization-chart`, {
        params,
        withCredentials: true,
      });
      setChartData(res.data);
      setExpandedNodes(collectNodeIds(res.data?.nodes || []));
    } catch (err) {
      console.error(err);
      setError(getApiErrorMessage(err, 'Failed to load organization chart.'));
      setChartData(null);
    } finally {
      setLoading(false);
    }
  }, [filters.organization, filters.department, filters.team, filters.locationType, filters.active, debouncedSearch]);

  useEffect(() => {
    fetchChart();
  }, [fetchChart]);

  const metadata = chartData?.metadata || {};
  const filterOptions = chartData?.filters || {};

  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const expandAll = () => setExpandedNodes(collectNodeIds(chartData?.nodes || []));
  const collapseAll = () => setExpandedNodes(new Set());

  const handleNavigate = (node) => {
    if (node?.nodeType === 'user' && node.id) {
      navigate(`/dashboard/user/${node.id}`);
    }
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await chartRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const fitToScreen = () => setZoom(1);

  const summaryCards = useMemo(() => ([
    { label: 'Total Users', value: metadata.totalUsers || 0 },
    { label: 'Placed', value: metadata.placedUsers || 0 },
    { label: 'By Manager', value: metadata.placedByManager || 0 },
    { label: 'By Dept/Team', value: metadata.placedByDepartmentTeam || 0 },
    { label: 'Pending', value: metadata.unassignedUsers || 0 },
  ]), [metadata]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="organization-chart-page">
        <SpinnerGap size={36} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4" data-testid="organization-chart-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Organization Chart</h1>
          <p className="text-slate-500 mt-1 text-sm">Read-only hierarchy generated from existing portal users and teams.</p>
        </div>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-red-700">{error}</CardContent>
        </Card>
        <Button onClick={fetchChart}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="organization-chart-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <TreeStructure size={24} className="text-blue-600" />
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Organization Chart</h1>
          </div>
          <p className="text-slate-500 mt-1 text-sm">
            Generated from existing users, organizations, teams, roles, designations, and reporting-manager relationships.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={expandAll} data-testid="org-chart-expand-all">
            <CaretDoubleDown size={16} className="mr-1" /> Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} data-testid="org-chart-collapse-all">
            <CaretDoubleUp size={16} className="mr-1" /> Collapse All
          </Button>
          <Button variant="outline" size="sm" onClick={() => setLayout((prev) => (prev === 'vertical' ? 'horizontal' : 'vertical'))}>
            {layout === 'vertical' ? <ArrowsHorizontal size={16} className="mr-1" /> : <ArrowsVertical size={16} className="mr-1" />}
            {layout === 'vertical' ? 'Left-Right Flow' : 'Top-Down Tree'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setZoom((z) => Math.min(z + 0.1, 1.8))}>
            <MagnifyingGlassPlus size={16} className="mr-1" /> Zoom In
          </Button>
          <Button variant="outline" size="sm" onClick={() => setZoom((z) => Math.max(z - 0.1, 0.5))}>
            <MagnifyingGlassMinus size={16} className="mr-1" /> Zoom Out
          </Button>
          <Button variant="outline" size="sm" onClick={fitToScreen}>Fit</Button>
          <Button variant="outline" size="sm" onClick={toggleFullscreen}>
            {isFullscreen ? <ArrowsIn size={16} className="mr-1" /> : <ArrowsOut size={16} className="mr-1" />}
            Full Screen
          </Button>
        </div>
      </div>

      <OrganizationChartFilters filters={filterOptions} values={filters} onChange={setFilters} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {summaryCards.map((card) => (
          <Card key={card.label} className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">{card.label}</p>
              <p className="text-2xl font-bold text-slate-800">{card.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {(metadata.missingManager > 0 || metadata.missingDesignation > 0 || metadata.circularRelationships > 0) && (
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-4 text-sm text-slate-600 space-y-1">
            <p className="font-medium text-slate-800">Data quality summary</p>
            <p>{metadata.missingManager || 0} users missing manager relationships.</p>
            <p>{metadata.missingDesignation || 0} users missing designation.</p>
            <p>{metadata.missingDepartment || 0} users with unresolved department grouping.</p>
            {metadata.circularRelationships > 0 && (
              <p>{metadata.circularRelationships} circular manager relationship{metadata.circularRelationships === 1 ? '' : 's'} skipped.</p>
            )}
          </CardContent>
        </Card>
      )}

      <div
        ref={chartRef}
        className="overflow-auto rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100/80 p-8 min-h-[480px]"
        data-testid="org-chart-canvas"
      >
        <div
          className="inline-flex justify-center min-w-full"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', width: `${100 / zoom}%` }}
        >
          <OrganizationChart
            nodes={chartData?.nodes || []}
            expandedNodes={expandedNodes}
            onToggle={toggleNode}
            layout={layout}
            onNavigate={handleNavigate}
          />
        </div>
      </div>

      <UnassignedUsersPanel users={chartData?.unassignedUsers || []} onNavigate={handleNavigate} />
    </div>
  );
};

export default OrganizationChartPage;
